import {
  Button,
  Divider,
  Flex,
  Progress,
  theme,
  Tooltip,
  Typography
} from 'antd'
import { Check, ShieldAlert, ShieldCheck, X } from 'lucide-react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { IconWithBackground, StatusPill, toneShades } from 'src/components'
import type { StatusTone } from 'src/components'
import { useThemeMode } from 'src/theme'

import { buildApprovalDetail } from './formatApprovalDetail'
import { useStyles } from './styles'
import type { ApprovalResolution } from './types'
import { humaniseTool } from './utils'

const { Text } = Typography

const SEVERITY_TONE: Record<string, StatusTone> = {
  none: 'gray',
  low: 'green',
  medium: 'gold',
  high: 'orange',
  critical: 'red'
}

const PILL_FONT_SIZE = 13
const HEADER_ICON_SIZE = 28

type Props = {
  tool: string
  value?: Record<string, unknown>
  disabled: boolean
  enter?: boolean
  onResolve: (action: ApprovalResolution) => void
}

export const ApprovalCard = ({
  tool,
  value,
  disabled,
  enter,
  onResolve
}: Props) => {
  const { styles, cx } = useStyles()
  const { token } = theme.useToken()
  const { mode } = useThemeMode()
  const { t } = useTranslation()
  const detail = buildApprovalDetail(value)
  const tone: StatusTone | undefined = detail.severity
    ? (SEVERITY_TONE[detail.severity] ?? 'gray')
    : undefined
  const shades = tone ? toneShades(tone, mode) : undefined

  return (
    <div
      className={cx(styles.approvalCard, enter && styles.enterSlow)}
      style={
        shades
          ? { background: shades.wash, borderColor: shades.border }
          : undefined
      }
    >
      <Flex align="center" gap={8} wrap className={styles.approvalHeader}>
        <IconWithBackground
          shape="square"
          size={HEADER_ICON_SIZE}
          color={shades ? shades.solid : token.colorTextSecondary}
        >
          <ShieldAlert size={16} />
        </IconWithBackground>
        <div className={styles.approvalHeaderText}>
          <span className={styles.approvalKicker}>
            {t('workspaceHome.approval.actionRequest')}
          </span>
          <Text strong className={styles.approvalToolName}>
            {tool}()
          </Text>
        </div>
        {detail.severity && tone && (
          <Tooltip title={t('workspaceHome.approval.severityHint')}>
            <StatusPill tone={tone} style={{ fontSize: PILL_FONT_SIZE }}>
              {t(
                `workspaceHome.approval.severity.${detail.severity}`,
                humaniseTool(detail.severity)
              )}
            </StatusPill>
          </Tooltip>
        )}
        {detail.destructive && (
          <StatusPill tone="red" style={{ fontSize: PILL_FONT_SIZE }}>
            {t('workspaceHome.approval.destructive')}
          </StatusPill>
        )}
      </Flex>

      {detail.confidence !== undefined && (
        <Tooltip title={t('workspaceHome.approval.confidenceHint')}>
          <div className={styles.confidenceRow}>
            <span className={styles.approvalKicker}>
              {t('workspaceHome.approval.confidenceLabel')}
            </span>
            <Progress
              percent={detail.confidence}
              showInfo={false}
              size="small"
              strokeColor={token.colorPrimary}
              className={styles.confidenceBar}
            />
            <Text className={styles.confidenceValue}>{detail.confidence}%</Text>
          </div>
        </Tooltip>
      )}

      {detail.confidence !== undefined &&
        (detail.whatWillHappen || detail.rows.length > 0) && (
          <Divider className={styles.approvalDivider} />
        )}

      {(detail.whatWillHappen || detail.rows.length > 0) && (
        <div className={styles.approvalWhat}>
          <span className={styles.approvalKicker}>
            {t('workspaceHome.approval.whatWillHappen')}
          </span>
          {detail.whatWillHappen?.map((line) => (
            <Text key={line} className={styles.approvalWhatText}>
              {line}
            </Text>
          ))}
          {detail.rows.length > 0 && (
            <div className={styles.approvalArgs}>
              {detail.rows.map((row) => (
                <Fragment key={row.key}>
                  <span className={styles.approvalArgLabel}>{row.label}</span>
                  <span className={styles.approvalArgValue}>{row.value}</span>
                </Fragment>
              ))}
            </div>
          )}
        </div>
      )}

      <Flex gap={8} wrap className={styles.approvalActions}>
        <Button
          type="primary"
          size="small"
          icon={<Check size={14} />}
          disabled={disabled}
          onClick={() => onResolve('accept')}
        >
          {t('workspaceHome.approval.approve')}
        </Button>
        <Tooltip
          title={t('workspaceHome.approval.alwaysHint', {
            tool: humaniseTool(tool)
          })}
        >
          <Button
            size="small"
            icon={<ShieldCheck size={14} />}
            disabled={disabled}
            onClick={() => onResolve('accept_always')}
          >
            {t('workspaceHome.approval.always')}
          </Button>
        </Tooltip>
        <Button
          danger
          size="small"
          icon={<X size={14} />}
          disabled={disabled}
          onClick={() => onResolve('reject')}
        >
          {t('workspaceHome.approval.reject')}
        </Button>
      </Flex>
    </div>
  )
}
